import { useEffect, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsGeneral';
import DireccionesUnidadesOrgModal from '../DireccionesUnidadesOrg/DireccionesUnidadesOrgModal';
import ZonasGeograficasModal from '../ZonaGeografica/ZonasGeograficasModal';
import TipoUnidadOrgModal from '../TipoUnidadOrg/TipoUnidadOrgModal';
import { normalizeValue } from '../../../../../utils/Texts';

const EMPTY = {
  idZonaGeografica: 0,
  idDireccion: 0, // Se mantiene internamente para no perder el link al guardar
  clave: '',
  nombre: '',
  idTipoUnidadOrganizacional: 0,
  descripcion: '',
  idEmpresa: 0,
};

export default function Modal({
  open,
  mode = 'new',
  data = null,
  onClose,
  onSubmit,
  inline = false,
}) {
  const [form, setForm] = useState(EMPTY);

  // 👉 catálogos para los selects
  const [zonas, setZonas] = useState([]);
  const [direcciones, setDirecciones] = useState([]);
  const [tiposUnidadOrg, setTiposUnidadOrg] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loadingCats, setLoadingCats] = useState(false);
  const [errorCats, setErrorCats] = useState(null);
  const [zonasOpen, setZonasOpen] = useState(false);
  const [tiposOpen, setTiposOpen] = useState(false);
  const [savingZona, setSavingZona] = useState(false);
  const [savingTipo, setSavingTipo] = useState(false);

  const [errors, setErrors] = useState({
    idZonaGeografica: '',
    clave: '',
    nombre: '',
    idTipoUnidadOrganizacional: '',
    descripcion: '',
    idEmpresa: '',
  });

  const validateField = (field, rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    let error = '';

    switch (field) {
      case 'idZonaGeografica':
        if (!value || value <= 0) error = 'Selecciona una zona geográfica.';
        break;
      case 'idDireccion':
        // No obligatorio al crear el registro inicial
        break;
      case 'clave':
        if (!value) error = 'La clave es obligatoria.';
        break;
      case 'nombre':
        if (!value) error = 'El nombre es obligatorio.';
        break;
      case 'idTipoUnidadOrganizacional':
        if (!value || value <= 0) error = 'Selecciona un tipo de unidad.';
        break;
      case 'descripcion':
        if (!value) error = 'La descripción es obligatoria.';
        break;
      case 'idEmpresa':
        if (!value || value <= 0) error = 'Selecciona una empresa.';
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateAll = () => {
    const fields = ['idZonaGeografica', 'clave', 'nombre', 'idTipoUnidadOrganizacional', 'descripcion', 'idEmpresa'];
    let isValid = true;
    fields.forEach(f => {
      if (!validateField(f, form[f])) isValid = false;
    });
    return isValid;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  // Carga datos de edición
  useEffect(() => {
    const initForm = async () => {
      if (mode === 'edit' && data) {
        let relIdEmpresa = 0;
        try {
          // Intentar obtener la relación con la empresa
          const resRel = await axios.get(ENDPOINTS.empresasUnidadesOrg);
          const allRels = (resRel.data || []).map(Models.empresasUnidadesOrg);
          const myRel = allRels.find(r => r.idUnidadOrganizacional === data.idUnidadOrganizacional);
          if (myRel) relIdEmpresa = myRel.idEmpresa;
        } catch (e) {
          console.error("Error fetching relationship:", e);
        }

        const filled = {
          idZonaGeografica: data.idZonaGeografica ?? 0,
          idDireccion: data.idDireccion ?? 0,
          clave: data.clave ?? '',
          nombre: data.nombre ?? '',
          idTipoUnidadOrganizacional:
            data.idTipoUnidadOrganizacional ??
            data.idTipoUndiadOrganizacional ??
            data.idTipoUnidadOrg ??
            0,
          descripcion: data.descripcion ?? '',
          idEmpresa: relIdEmpresa,
        };
        setForm(filled);
        // Validar campos precargados
        validateField('idZonaGeografica', filled.idZonaGeografica);
        validateField('clave', filled.clave);
        validateField('nombre', filled.nombre);
        validateField('idTipoUnidadOrganizacional', filled.idTipoUnidadOrganizacional);
        validateField('descripcion', filled.descripcion);
        validateField('idEmpresa', filled.idEmpresa);
      } else {
        setForm(EMPTY);
        setErrors({
          idZonaGeografica: '',
          clave: '',
          nombre: '',
          idTipoUnidadOrganizacional: '',
          descripcion: '',
          idEmpresa: '',
        });
      }
    };
    initForm();
  }, [mode, data]);

  // Cargar catálogos cuando se abre el modal
  const loadCatalogs = async () => {
    setLoadingCats(true);
    setErrorCats(null);
    try {
      const [resZonas, resDirecciones, resTipos, resEmpresas] = await Promise.all([
        axios.get(ENDPOINTS.zonasGeograficas),
        axios.get(ENDPOINTS.direccionesUnidadesOrg),
        axios.get(ENDPOINTS.tipoUnidadOrg),
        axios.get(ENDPOINTS.empresas),
      ]);

      const rawZonas = Array.isArray(resZonas.data)
        ? resZonas.data
        : Array.isArray(resZonas.data?.data)
          ? resZonas.data.data
          : [];
      const zonasNorm = rawZonas.map((z) => Models.zonasGeograficas(z));

      const rawDirecciones = Array.isArray(resDirecciones.data)
        ? resDirecciones.data
        : Array.isArray(resDirecciones.data?.data)
          ? resDirecciones.data.data
          : [];
      const dirsNorm = rawDirecciones.map((d) => Models.direccionesUnidadesOrg(d));

      const rawTipos = Array.isArray(resTipos.data)
        ? resTipos.data
        : Array.isArray(resTipos.data?.data)
          ? resTipos.data.data
          : [];
      const tiposNorm = rawTipos.map((t) => Models.tipoUnidadOrg(t));

      const rawEmpresas = (resEmpresas.data || []).map(Models.empresas);

      setZonas(zonasNorm);
      setDirecciones(dirsNorm);
      setTiposUnidadOrg(tiposNorm);
      setEmpresas(rawEmpresas);
    } catch (err) {
      console.error('Error cargando catálogos UnidadesOrg', err);
      setErrorCats('No se pudieron cargar los catálogos.');
    } finally {
      setLoadingCats(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    loadCatalogs();
  }, [open]);

  const handleSubmitNuevaZona = async (zonaForm) => {
    try {
      setSavingZona(true);
      const res = await axios.post(ENDPOINTS.zonasGeograficas, zonaForm);
      const created = res?.data || {};

      // Refrescar catálogo
      await loadCatalogs();

      // Extraer el ID de forma robusta
      let newId = 0;
      if (typeof created === 'number') {
        newId = created;
      } else if (typeof created === 'object' && created !== null) {
        newId = created.idZonaGeografica ?? created.IdZonaGeografica ?? created.id ?? created.Id ?? 0;
      }

      if (newId && newId > 0) {
        // Usar setTimeout para asegurar que el select se haya actualizado con las nuevas opciones
        setTimeout(() => {
          setForm((f) => ({ ...f, idZonaGeografica: newId }));
          validateField('idZonaGeografica', newId);
        }, 100);
      }
      setZonasOpen(false);
    } catch (e) {
      console.error(e);
      alert('No se pudo crear la zona geográfica.');
    } finally {
      setSavingZona(false);
    }
  };

  const handleSubmitNuevoTipo = async (tipoForm) => {
    try {
      setSavingTipo(true);
      const res = await axios.post(ENDPOINTS.tipoUnidadOrg, tipoForm);
      const created = res?.data || {};

      // Refrescar catálogo
      await loadCatalogs();

      // Extraer el ID de forma robusta
      let newId = 0;
      if (typeof created === 'number') {
        newId = created;
      } else if (typeof created === 'object' && created !== null) {
        newId =
          created.idTipoUnidadOrganizacional ??
          created.IdTipoUnidadOrganizacional ??
          created.idTipoUndiadOrganizacional ??
          created.idTipoUnidadOrg ??
          created.id ?? 
          created.Id ?? 0;
      }

      if (newId && newId > 0) {
        // Usar setTimeout para asegurar que el select se haya actualizado con las nuevas opciones
        setTimeout(() => {
          setForm((f) => ({ ...f, idTipoUnidadOrganizacional: newId }));
          validateField('idTipoUnidadOrganizacional', newId);
        }, 100);
      }
      setTiposOpen(false);
    } catch (e) {
      console.error(e);
      alert('No se pudo crear el tipo de unidad organizacional.');
    } finally {
      setSavingTipo(false);
    }
  };

  if (!open) return null;

  const hasErrors = Object.values(errors).some((e) => !!e);
  const isSaveDisabled =
    !form.idZonaGeografica ||
    !form.clave?.trim() ||
    !form.nombre?.trim() ||
    !form.idTipoUnidadOrganizacional ||
    !form.descripcion?.trim() ||
    hasErrors;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '4px',
  };

  // Helpers de formato (mismos criterios que el Catalog)
  function labelZona(z) {
    if (!z) return '';
    return (
      (z.clave && z.nombre
        ? `${z.clave} - ${z.nombre}`
        : z.nombre || z.clave) || `Zona ${z.idZonaGeografica}`
    );
  }

  function labelDireccion(d) {
    if (!d) return '';
    const parts = [];

    const loc = [d.estado, d.municipio, d.colonia].filter(Boolean).join(', ');
    if (loc) parts.push(loc);

    if (d.calle) {
      let calle = d.calle;
      if (d.numeroExterior) calle += ` ${d.numeroExterior}`;
      parts.push(calle);
    }

    return parts.join(' · ');
  }

  function labelTipoUnidadOrg(t) {
    if (!t) return '';
    const id = t.idTipoUnidadOrganizacional ?? t.idTipoUndiadOrganizacional;
    return (
      (t.clave && t.nombre
        ? `${t.clave} - ${t.nombre}`
        : t.nombre || t.clave) || `Tipo ${id}`
    );
  }

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nueva unidad organizacional' : 'Editar unidad organizacional'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '15%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      {/* Identidad de la Unidad */}
      <div style={{ gridColumn: 'span 2' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Nombre:</label>
        <br /><br />
        <input
          className={`infoField${errors.nombre ? ' infoField--error' : ''}`}
          value={form.nombre}
          onChange={(e) => handleChange('nombre', e.target.value)}
          maxLength={100}
        />
        {errors.nombre && <small style={errorStyle}>{errors.nombre}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Clave:</label>
        <br /><br />
        <input
          className={`infoField${errors.clave ? ' infoField--error' : ''}`}
          value={form.clave}
          onChange={(e) => handleChange('clave', e.target.value)}
          maxLength={20}
        />
        {errors.clave && <small style={errorStyle}>{errors.clave}</small>}
      </div>

      {/* Pertenencia Organizacional */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Empresa vinculada:</label>
        <br /><br />
        <select
          className={`infoField${errors.idEmpresa ? ' infoField--error' : ''}`}
          value={form.idEmpresa || 0}
          onChange={(e) => handleChange('idEmpresa', Number(e.target.value) || 0)}
        >
          <option value={0} disabled>
            {loadingCats ? 'Cargando empresas…' : 'Seleccione una empresa'}
          </option>
          {empresas.map((emp) => (
            <option key={emp.idEmpresa} value={emp.idEmpresa}>
              {normalizeValue(emp.nombre)}
            </option>
          ))}
        </select>
        {errors.idEmpresa && <small style={errorStyle}>{errors.idEmpresa}</small>}
      </div>

      {/* Clasificación Logística y Funcional */}
      <div>
        <label
          className="roles-title"
          style={{ fontSize: '.95rem', display: 'flex', alignItems: 'center', gap: 8 }}
        >
          Tipo unidad:
          <button
            type="button"
            className="btn-action"
            onClick={() => setTiposOpen(true)}
            title="Crear nuevo tipo"
            style={{ padding: '2px 8px', lineHeight: 1 }}
          >
            ＋ Tipo
          </button>
        </label>
        <br /><br />
        <select
          className={`infoField${errors.idTipoUnidadOrganizacional ? ' infoField--error' : ''}`}
          value={form.idTipoUnidadOrganizacional || 0}
          onChange={(e) => handleChange('idTipoUnidadOrganizacional', Number(e.target.value) || 0)}
        >
          <option value={0} disabled>
            {loadingCats ? 'Cargando tipos…' : 'Seleccione un tipo'}
          </option>
          {tiposUnidadOrg.map((t) => {
            const id =
              t.idTipoUnidadOrganizacional ??
              t.idTipoUndiadOrganizacional ??
              t.idTipoUnidadOrg;
            return (
              <option key={id} value={id}>
                {labelTipoUnidadOrg(t)}
              </option>
            );
          })}
        </select>
        {errors.idTipoUnidadOrganizacional && <small style={errorStyle}>{errors.idTipoUnidadOrganizacional}</small>}
      </div>

      <div>
        <label
          className="roles-title"
          style={{ fontSize: '.95rem', display: 'flex', alignItems: 'center', gap: 8 }}
        >
          Zona:
          <button
            type="button"
            className="btn-action"
            onClick={() => setZonasOpen(true)}
            title="Crear nueva zona"
            style={{ padding: '2px 8px', lineHeight: 1 }}
          >
            ＋ Zona
          </button>
        </label>
        <br /><br />
        <select
          className={`infoField${errors.idZonaGeografica ? ' infoField--error' : ''}`}
          value={form.idZonaGeografica || 0}
          onChange={(e) => handleChange('idZonaGeografica', Number(e.target.value) || 0)}
        >
          <option value={0} disabled>
            {loadingCats ? 'Cargando zonas…' : 'Seleccione una zona'}
          </option>
          {zonas.map((z) => (
            <option key={z.idZonaGeografica} value={z.idZonaGeografica}>
              {labelZona(z)}
            </option>
          ))}
        </select>
        {errors.idZonaGeografica && <small style={errorStyle}>{errors.idZonaGeografica}</small>}
      </div>

      {/* Información Adicional */}
      <div style={{ gridColumn: 'span 2' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Descripción:</label>
        <br /><br />
        <textarea
          className={`infoField${errors.descripcion ? ' infoField--error' : ''}`}
          value={form.descripcion}
          onChange={(e) => handleChange('descripcion', e.target.value)}
          maxLength={255}
          rows={3}
        />
        {errors.descripcion && <small style={errorStyle}>{errors.descripcion}</small>}
      </div>

      {errorCats && (
        <div style={{ gridColumn: '1 / -1', marginTop: '.4rem' }}>
          <p style={{ color: '#b91c1c', fontSize: '.8rem' }}>{errorCats}</p>
        </div>
      )}
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={() => {
          if (validateAll()) onSubmit(form, mode);
        }}
        disabled={isSaveDisabled}
        style={{
          opacity: isSaveDisabled ? 0.5 : 1,
          cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        {mode === 'new' ? 'Guardar' : 'Actualizar'}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}

        <ZonasGeograficasModal
          open={zonasOpen}
          mode="new"
          onClose={() => setZonasOpen(false)}
          onSubmit={handleSubmitNuevaZona}
        />
        <TipoUnidadOrgModal
          open={tiposOpen}
          mode="new"
          onClose={() => setTiposOpen(false)}
          onSubmit={handleSubmitNuevoTipo}
        />
        {(savingZona || savingTipo) && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.1)' }} />
        )}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(900px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>

      <ZonasGeograficasModal
        open={zonasOpen}
        mode="new"
        onClose={() => setZonasOpen(false)}
        onSubmit={handleSubmitNuevaZona}
      />
      <TipoUnidadOrgModal
        open={tiposOpen}
        mode="new"
        onClose={() => setTiposOpen(false)}
        onSubmit={handleSubmitNuevoTipo}
      />
      {(savingZona || savingTipo) && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.1)' }} />
      )}
    </div>
  );
}
